import { useEffect } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import { Box, Heading, Text } from '@chakra-ui/react';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  KeyOpsGhostButton,
  KeyOpsPrimaryButton,
} from '../../components/ButtonComponents';
import Loading from '../../components/Loading';
import { useEvaluationEventById } from '../../hooks/useEvaluationEventById';
import { useUpdateEvaluationEvent } from '../../hooks/useUpdateEvaluationEvent';
import i18n from '../../utils/i18n';
import { EVALUATION_EVENTS } from '../../utils/internal-routes.const';

import ContentSection from './components/ContentSection';
import DetailsSection from './components/DetailsSection';
import VenueSection from './components/VenueSection';
import {
  EvaluationEventEditFormData,
  evaluationEventEditFormSchema,
  getDefaultEvaluationEventEditFormValues,
  mapEventDataToFormDefaults,
  transformFormValues,
} from './evaluation-event-edit-form-utils';

export const EvaluationEventEdit = () => {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId?: string }>();
  const { data: eventData, isLoading } = useEvaluationEventById(eventId ?? '');
  const { mutate: updateEvaluationEvent } = useUpdateEvaluationEvent();

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<EvaluationEventEditFormData>({
    resolver: zodResolver(evaluationEventEditFormSchema),
    defaultValues: getDefaultEvaluationEventEditFormValues(),
  });

  const {
    fields: speakersFields,
    append: appendSpeaker,
    remove: removeSpeaker,
    move: moveSpeaker,
  } = useFieldArray({ control, name: 'speakers' });

  const {
    fields: moderatorsFields,
    append: appendModerator,
    remove: removeModerator,
    move: moveModerator,
  } = useFieldArray({ control, name: 'moderators' });

  const {
    fields: learningObjectivesFields,
    append: appendLearningObjectives,
    remove: removeLearningObjectives,
    move: moveLearningObjectives,
  } = useFieldArray({ control, name: 'learningObjectives' });

  useEffect(() => {
    if (eventData) {
      reset(mapEventDataToFormDefaults(eventData));
    }
  }, [eventData, reset]);

  const onSubmit = (values: EvaluationEventEditFormData) => {
    if (!eventId) return;
    updateEvaluationEvent({
      evaluationEventId: eventId,
      newEvaluationEventData: transformFormValues(values),
    });
  };

  if (isLoading) return <Loading />;
  if (!eventId || !eventData)
    return <Text>{i18n.t('evaluationEvent.eventNotFound')}</Text>;

  return (
    <Box mx={20}>
      <KeyOpsGhostButton
        leftIcon={<ChevronLeftIcon />}
        onClick={() => navigate(EVALUATION_EVENTS)}
      >
        {i18n.t('common.back')}
      </KeyOpsGhostButton>

      <Heading size="lg" mb={6}>
        {i18n.t('evaluationEvent.editHeading')}
      </Heading>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* --- DETAILS --- */}
        <DetailsSection
          control={control}
          register={register}
          errors={errors}
          eventData={eventData}
        />
        {/* --- CONTENT --- */}
        <ContentSection
          errors={errors}
          register={register}
          speakersFieldArray={{
            fields: speakersFields,
            append: appendSpeaker,
            remove: removeSpeaker,
            move: moveSpeaker,
          }}
          moderatorsFieldArray={{
            fields: moderatorsFields,
            append: appendModerator,
            remove: removeModerator,
            move: moveModerator,
          }}
          learningObjectivesFieldArray={{
            fields: learningObjectivesFields,
            append: appendLearningObjectives,
            remove: removeLearningObjectives,
            move: moveLearningObjectives,
          }}
          eventData={eventData}
        />
        {/* --- VENUE --- */}
        <VenueSection
          register={register}
          errors={errors}
          eventData={eventData}
        />
        {/* --- UPDATE BUTTON --- */}
        <KeyOpsPrimaryButton
          data-testid="evaluation-edit-form-submit-button"
          type="submit"
          isLoading={isSubmitting}
          size={'sm'}
          w={'100%'}
        >
          {i18n.t('common.update')}
        </KeyOpsPrimaryButton>
      </form>
    </Box>
  );
};
